import React, { FC } from 'react';

import { css, useTheme } from '@emotion/react';
import { themeGet } from '@lucid/theme';
import * as CSS from 'csstype';

import ArrowRight from './icons/arrow-right';
import CarotDown from './icons/carot-down';
import CarotRight from './icons/carot-right';
import Checkmark from './icons/checkmark';
import Play from './icons/play';
import RSS from './icons/rss';
import World from './icons/world';
import X from './icons/x';

export type IconType = 'ArrowRight'
  | 'CarotRight'
  | 'CarotDown'
  | 'Checkmark'
  | 'Play'
  | 'RSS'
  | 'World'
  | 'X';

export const iconTypes = [
  'ArrowRight',
  'CarotRight',
  'CarotDown',
  'Checkmark',
  'Play',
  'RSS',
  'World',
  'X'
];

export const iconFlip = [
  'vertical',
  'horizontal',
  'both'
];

export const iconSize = [
  'auto',
  'responsive'
];

type Flip = 'vertical' | 'horizontal' | 'both';
type Sizing = 'auto' | 'responsive';

export interface IconProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The available icons to be rendered. */
  type: IconType;
  /** Sets the color of the icon. */
  color?: string | CSS.Property.Color;
  /** Sets the color when hovered for the icon. */
  hoverColor?: string;
  /** Changes the direction of the icon. */
  flip?: Flip;
  /** Sets how the icon should size when rendered. */
  sizing?: Sizing;
}

const getFlip = (flip?: Flip) => {
  if (!flip) {
    return {};
  }

  switch (flip) {
    case 'horizontal':
      return {
        transform: 'scaleX(-1)'
      };

    case 'vertical':
      return {
        transform: 'scaleY(-1)'
      };

    case 'both':
      return {
        transform: 'scale(-1, -1)'
      };
  }
};

const Icon: FC<IconProps> = ({
  className,
  type = 'CarotRight',
  color,
  hoverColor,
  sizing = 'auto',
  flip
}) => {
  const isResponsiveSizing = sizing === 'responsive';
  const verticalAlign = isResponsiveSizing ? { verticalAlign: '-0.125em' } : {};
  const transform = getFlip(flip);
  const theme = useTheme();

  let themeColor = color;

  if (color?.includes('.')) {
    themeColor = `${themeGet(`colors.${color}`, 'primary')({theme})}`;
  }

  if (color === 'primary') {
    themeColor = theme.colors.primary;
  }

  if (color === 'secondary') {
    themeColor = theme.colors.secondary;
  }

  const iconPathFillCSS = css({
    path: {
      fill: themeColor ? themeColor : 'currentColor'
    },
    '&:hover path': {
      fill: hoverColor ? hoverColor : ''
    }
  });

  const iconCSS = css([
    {
      label: 'svg-icon'
    },
    verticalAlign,
    {
      display: 'inline-block',
      height: isResponsiveSizing ? '100%' : '1em',
      width: isResponsiveSizing ? '100%' : '1em',
      position: 'relative'
    },
    transform
  ]);

  switch (type) {
    case 'ArrowRight':
      return (
        <span className={className} css={[iconCSS, iconPathFillCSS]}>
          <ArrowRight color={themeColor} />
        </span>
      );

    case 'CarotRight':
      return (
        <span className={className} css={[iconCSS, iconPathFillCSS]}>
          <CarotRight color={themeColor} />
        </span>
      );

    case 'CarotDown':
      return (
        <span className={className} css={[iconCSS, iconPathFillCSS]}>
          <CarotDown color={themeColor} />
        </span>
      );

    case 'Checkmark':
      return (
        <span className={className} css={[iconCSS, iconPathFillCSS]}>
          <Checkmark color={themeColor} />
        </span>
      );

    case 'Play':
      return (
        <span className={className} css={[iconCSS, iconPathFillCSS]}>
          <Play color={themeColor} />
        </span>
      );

    case 'RSS':
      return (
        <span className={className} css={[iconCSS, iconPathFillCSS]}>
          <RSS color={themeColor} />
        </span>
      );

    case 'World':
      return (
        <span className={className} css={iconCSS}>
          <World color={themeColor} />
        </span>
      );

    case 'X':
      return (
        <span className={className} css={[iconCSS, iconPathFillCSS]}>
          <X color={themeColor} />
        </span>
      );

    default:
      return <svg />;
  }
};

export { Icon };
export default Icon;
